import { FC, useState, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { RadioButton } from 'primereact/radiobutton';
import { Calendar } from 'primereact/calendar';
import { confirmPopup, ConfirmPopup } from 'primereact/confirmpopup';
import { FeeRulesVersionViewModel } from '../../models/fees';

type PublishMode = 'now' | 'future';

interface PublishVersionDialogProps {
  visible: boolean;
  onClose: () => void;
  onPublish: (description: string, publishDate?: string | null) => Promise<void>;
  onDeleteVersion: (id: number) => Promise<void>;
  onSelectVersion: (versionId: number) => void;
  versions: FeeRulesVersionViewModel[];
  draftConfigCount: number;
  draftHudLineCount: number;
}

export const PublishVersionDialog: FC<PublishVersionDialogProps> = ({
  visible,
  onClose,
  onPublish,
  onDeleteVersion,
  onSelectVersion,
  versions,
  draftConfigCount,
  draftHudLineCount,
}) => {
  const [showPublishForm, setShowPublishForm] = useState(false);
  const [description, setDescription] = useState('');
  const [publishMode, setPublishMode] = useState<PublishMode>('now');
  const [futureDate, setFutureDate] = useState<Date | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const minFutureDate = useMemo(() => {
    const date = new Date();
    date.setHours(date.getHours() + 1);
    date.setMinutes(date.getMinutes() + 5);
    return date;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, publishMode]);

  const sortedVersions = useMemo(() => {
    return [...versions].sort((a, b) => {
      const dateA = a.publishDate ? new Date(a.publishDate).getTime() : 0;
      const dateB = b.publishDate ? new Date(b.publishDate).getTime() : 0;
      return dateB - dateA;
    });
  }, [versions]);

  const isFuturePublication = (publishDate: string | null): boolean => {
    if (!publishDate) return false;
    return new Date(publishDate) > new Date();
  };

  const handlePublish = async () => {
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    if (publishMode === 'future') {
      if (!futureDate) {
        setError('A future date and time is required');
        return;
      }
      const oneHourFromNow = new Date();
      oneHourFromNow.setHours(oneHourFromNow.getHours() + 1);
      if (futureDate <= oneHourFromNow) {
        setError('Future publish date must be at least 1 hour from now');
        return;
      }
    }

    setError(null);
    setIsPublishing(true);

    try {
      const publishDate =
        publishMode === 'future' && futureDate ? futureDate.toISOString() : null;
      await onPublish(description.trim(), publishDate);
      resetPublishForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish version');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDelete = async (id: number) => {
    setIsDeleting(id);
    try {
      await onDeleteVersion(id);
    } catch {
      // Error is already handled (toast) by the parent callback
    } finally {
      setIsDeleting(null);
    }
  };

  const resetPublishForm = () => {
    setShowPublishForm(false);
    setDescription('');
    setPublishMode('now');
    setFutureDate(null);
    setError(null);
  };

  const handleClose = () => {
    if (!isPublishing && isDeleting === null) {
      resetPublishForm();
      onClose();
    }
  };

  const hasUnpublishedChanges = draftConfigCount > 0 || draftHudLineCount > 0;

  return (
    <Dialog
      header="Publications"
      visible={visible}
      onHide={handleClose}
      style={{ width: '750px' }}
      dismissableMask={!isPublishing && isDeleting === null}
      closable={!isPublishing && isDeleting === null}
    >
      <ConfirmPopup />
      <div className="flex flex-column gap-4">
        {/* Publish Button / Form */}
        {!showPublishForm ? (
          <div className="flex justify-content-start align-items-center">
            <Button
              icon="pi pi-plus"
              label="Publish"
              className="p-button-success p-button-sm"
              onClick={() => setShowPublishForm(true)}
              disabled={!hasUnpublishedChanges || isPublishing}
              style={!hasUnpublishedChanges ? { opacity: 0.3 } : {}}
            />
            {!hasUnpublishedChanges && (
              <span className="ml-2 text-sm text-gray-500">
                No unpublished changes to publish
              </span>
            )}
          </div>
        ) : (
          <div className="bg-blue-50 p-3 border-round flex flex-column gap-3">
            <div className="flex justify-content-between align-items-center">
              <span className="font-semibold text-sm">New Publication</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm p-button-secondary"
                onClick={resetPublishForm}
                disabled={isPublishing}
              />
            </div>

            <div className="text-sm text-gray-700">
              Publishing {draftConfigCount} configuration
              {draftConfigCount !== 1 ? 's' : ''} and {draftHudLineCount} HUD line
              {draftHudLineCount !== 1 ? 's' : ''}
            </div>

            {/* Publish Mode Selection */}
            <div className="flex flex-column gap-2">
              <label className="font-medium text-sm">When to publish</label>
              <div className="flex gap-4">
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="publish-now"
                    value="now"
                    checked={publishMode === 'now'}
                    onChange={() => {
                      setPublishMode('now');
                      setFutureDate(null);
                      setError(null);
                    }}
                    disabled={isPublishing}
                  />
                  <label htmlFor="publish-now" className="text-sm">
                    Now
                  </label>
                </div>
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="publish-future"
                    value="future"
                    checked={publishMode === 'future'}
                    onChange={() => {
                      setPublishMode('future');
                      setError(null);
                    }}
                    disabled={isPublishing}
                  />
                  <label htmlFor="publish-future" className="text-sm">
                    Future Date
                  </label>
                </div>
              </div>
            </div>

            {/* Future Date Picker */}
            {publishMode === 'future' && (
              <div className="flex flex-column gap-2">
                <label htmlFor="future-date" className="font-medium text-sm">
                  Publish Date & Time<span className="text-red-500">*</span>
                </label>
                <Calendar
                  id="future-date"
                  value={futureDate}
                  onChange={(e) => {
                    setFutureDate(e.value as Date | null);
                    setError(null);
                  }}
                  showTime
                  hourFormat="12"
                  minDate={minFutureDate}
                  placeholder="Select date and time"
                  className="w-full"
                  disabled={isPublishing}
                  appendTo="self"
                />
                <small className="text-gray-500">
                  Must be at least 1 hour from now
                </small>
              </div>
            )}

            {/* Description */}
            <div className="flex flex-column gap-2">
              <label htmlFor="version-description" className="font-medium text-sm">
                Description<span className="text-red-500">*</span>
              </label>
              <InputTextarea
                id="version-description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setError(null);
                }}
                placeholder="Enter a description..."
                rows={2}
                className="w-full border-1 border-solid border-gray-300"
                disabled={isPublishing}
              />
            </div>

            {error && <small className="p-error">{error}</small>}

            {/* Actions */}
            <div className="flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-secondary p-button-sm"
                onClick={resetPublishForm}
                disabled={isPublishing}
              />
              <Button
                label={publishMode === 'now' ? 'Publish Now' : 'Schedule Publication'}
                className="p-button-success p-button-sm"
                onClick={handlePublish}
                disabled={
                  isPublishing ||
                  !description.trim() ||
                  (publishMode === 'future' && !futureDate)
                }
                loading={isPublishing}
              />
            </div>
          </div>
        )}

        {/* Publications Grid */}
        <DataTable
          value={sortedVersions}
          paginator
          rows={10}
          stripedRows
          emptyMessage="No publications found"
          className="p-datatable-sm"
          sortField="publishDate"
          sortOrder={-1}
          rowClassName={(row: FeeRulesVersionViewModel) =>
            isFuturePublication(row.publishDate) ? '' : 'cursor-pointer'
          }
          onRowClick={(e) => {
            const row = e.data as FeeRulesVersionViewModel;
            if (isFuturePublication(row.publishDate)) return;
            onSelectVersion(row.feeRulesVersionId);
            handleClose();
          }}
        >
          <Column
            field="publishDate"
            header="Date"
            body={(row: FeeRulesVersionViewModel) => {
              if (!row.publishDate) return '-';
              const date = new Date(row.publishDate);
              const formatted = date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              });
              const isFuture = isFuturePublication(row.publishDate);
              return (
                <div className="flex align-items-center gap-2">
                  <span>{formatted}</span>
                  {isFuture && (
                    <span
                      className="text-xs font-semibold px-2 py-1 border-round"
                      style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                    >
                      Scheduled
                    </span>
                  )}
                </div>
              );
            }}
            sortable
            style={{ minWidth: '220px' }}
          />
          <Column
            field="versionNumber"
            header="Version"
            style={{ width: '80px' }}
            sortable
          />
          <Column
            field="description"
            header="Description"
            style={{ minWidth: '200px' }}
            body={(row: FeeRulesVersionViewModel) => row.description || '-'}
          />
          <Column
            header="Actions"
            style={{ width: '80px' }}
            body={(row: FeeRulesVersionViewModel) => {
              const isFuture = isFuturePublication(row.publishDate);
              if (!isFuture) return null;
              return (
                <Button
                  icon="pi pi-trash"
                  className="p-button-text p-button-sm p-button-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmPopup({
                      target: e.currentTarget,
                      message:
                        'Are you sure you want to delete this scheduled publication?',
                      icon: 'pi pi-exclamation-triangle',
                      acceptClassName: 'p-button-danger p-button-sm',
                      accept: () => handleDelete(row.feeRulesVersionId),
                      reject: () => {},
                    });
                  }}
                  disabled={isDeleting === row.feeRulesVersionId}
                  loading={isDeleting === row.feeRulesVersionId}
                  tooltip="Delete scheduled publication"
                  tooltipOptions={{ position: 'top' }}
                />
              );
            }}
          />
        </DataTable>
      </div>
    </Dialog>
  );
};

